import z from "zod";

export const phase4AiTaskVersions = {
  pdfParse: "phase4.pdf_parse.v1",
  masterPlan: "phase4.master_plan.v1",
  targetPlan: "phase4.target_plan.v1",
  itemEdit: "phase4.item_edit.v1",
  masterPdfMerge: "phase4.master_pdf_merge.v1",
} as const;

export type Phase4AiTaskVersion = (typeof phase4AiTaskVersions)[keyof typeof phase4AiTaskVersions];

export const phase4AiTaskTypeValues = [
  "pdf_parse",
  "master_overall_check",
  "target_overall_check",
  "section_item_optimize",
  "master_pdf_merge",
] as const;

export type Phase4AiTaskType = (typeof phase4AiTaskTypeValues)[number];

export type Phase4AiTaskContract = {
  promptVersion: Phase4AiTaskVersion;
  schemaVersion: Phase4AiTaskVersion;
  taskType: Phase4AiTaskType;
};

export const phase4AiTaskContracts = {
  pdfParse: {
    promptVersion: phase4AiTaskVersions.pdfParse,
    schemaVersion: phase4AiTaskVersions.pdfParse,
    taskType: "pdf_parse",
  },
  masterPlan: {
    promptVersion: phase4AiTaskVersions.masterPlan,
    schemaVersion: phase4AiTaskVersions.masterPlan,
    taskType: "master_overall_check",
  },
  targetPlan: {
    promptVersion: phase4AiTaskVersions.targetPlan,
    schemaVersion: phase4AiTaskVersions.targetPlan,
    taskType: "target_overall_check",
  },
  itemEdit: {
    promptVersion: phase4AiTaskVersions.itemEdit,
    schemaVersion: phase4AiTaskVersions.itemEdit,
    taskType: "section_item_optimize",
  },
  masterPdfMerge: {
    promptVersion: phase4AiTaskVersions.masterPdfMerge,
    schemaVersion: phase4AiTaskVersions.masterPdfMerge,
    taskType: "master_pdf_merge",
  },
} as const satisfies Record<string, Phase4AiTaskContract>;

export const phase4ConfidenceSchema = z.enum(["high", "medium", "low"]);
export type Phase4Confidence = z.infer<typeof phase4ConfidenceSchema>;

export const phase4RequiredConfirmationSchema = z.preprocess((value) => {
  if (value === undefined || value === true || value === false || value === "true" || value === "false") {
    return true;
  }

  return value;
}, z.literal(true));

export function getPhase4OptimizationPlanContract(planType: "master" | "target") {
  return planType === "master" ? phase4AiTaskContracts.masterPlan : phase4AiTaskContracts.targetPlan;
}

export function buildPhase4TaskHeaderSchema(contract: Phase4AiTaskContract) {
  return z
    .object({
      schema_version: z.literal(contract.schemaVersion),
      prompt_version: z.literal(contract.promptVersion),
      task_type: z.literal(contract.taskType),
    })
    .strict();
}

export const phase4ReviewOutputFieldsSchema = z
  .object({
    summary: z.string().default(""),
    source_evidence: z.array(z.string()).default([]),
    protected_fact_warnings: z.array(z.string()).default([]),
    gaps: z.array(z.string()).default([]),
    warnings: z.array(z.string()).default([]),
    confidence: phase4ConfidenceSchema,
    requires_user_confirmation: phase4RequiredConfirmationSchema,
  })
  .strict();

export function buildPhase4ReviewOutputSchema(contract: Phase4AiTaskContract) {
  return buildPhase4TaskHeaderSchema(contract).merge(phase4ReviewOutputFieldsSchema);
}

export function buildPhase4ToolStringEnum(value: string) {
  return {
    type: "string",
    enum: [value],
  };
}

export function buildPhase4CommonToolProperties(contract: Phase4AiTaskContract) {
  return {
    schema_version: buildPhase4ToolStringEnum(contract.schemaVersion),
    prompt_version: buildPhase4ToolStringEnum(contract.promptVersion),
    task_type: buildPhase4ToolStringEnum(contract.taskType),
    summary: { type: "string" },
    source_evidence: {
      type: "array",
      items: { type: "string" },
    },
    protected_fact_warnings: {
      type: "array",
      items: { type: "string" },
    },
    gaps: {
      type: "array",
      items: { type: "string" },
    },
    warnings: {
      type: "array",
      items: { type: "string" },
    },
    confidence: {
      type: "string",
      enum: ["high", "medium", "low"],
    },
    requires_user_confirmation: {
      type: "boolean",
      enum: [true],
    },
  } as const;
}

export const phase4CommonToolRequiredFields = [
  "schema_version",
  "prompt_version",
  "task_type",
  "summary",
  "source_evidence",
  "protected_fact_warnings",
  "gaps",
  "warnings",
  "confidence",
  "requires_user_confirmation",
] as const;
